import { Field, useFormikContext } from 'formik'
import cloneDeep from 'lodash-es/cloneDeep'
import set from 'lodash-es/set'
import React from 'react'

import { WeightInput } from '@components'
import { StockTooltip } from '@components/common/StockTooltip'
import { Banner, ButtonClean, Checkbox, Input, Subsection } from '@fourthwall/components'
import { useAdminModeContext } from '@modules/App/providers/AdminModeProvider'

import type { ProductFormState } from '../../SingleProduct.types'
import { CompareAtPriceCheckboxField } from '../../components/CompareAtPriceCheckboxField'
import { ProductMembershipSettings } from '../../components/ProductMembershipSettings'
import { ProductVariantsData } from './components/ProductVariantsData'
import * as S from './styled'
import type { VariantsViewProps } from './types'

export const VariantsView = ({
  data,
  membershipTiers,
  viewConfig,
  onVariantTypeAdd,
  onStockChange,
  onIsCompareAtPriceEnabledValueChange,
}: VariantsViewProps) => {
  const adminMode = useAdminModeContext()

  const { values, setFieldValue } = useFormikContext<ProductFormState>()

  if (!data?.length) {
    return null
  }

  const handleSkuChange = (value: any) => {
    setFieldValue(
      'variants',
      cloneDeep(data).map((variant) => set(variant, 'sku', value)),
    )
  }

  const renderContent = () => {
    if (!viewConfig.showVariantsTable) {
      if (viewConfig.showManualStockNotice) {
        const hasQuantity = data.some(({ quantity }) => !!quantity)

        return <p>In stock: {hasQuantity ? 'Manually set by Partner Manager' : 'Unlimited'}</p>
      }

      const firstVariantQuantity = data[0].quantity

      const hasDisabledInventoryQuantity =
        !adminMode?.isActive &&
        (values.manufacturingService === 'FOURTHWALL_BESPOKE' ||
          values.fulfillmentService === 'CART_JAZZ')

      return (
        <S.SingleVariantContainer>
          {hasDisabledInventoryQuantity && (
            <S.SingleVariantItem>
              <Banner
                appearance="warning"
                title={
                  <>
                    Inventory quantity can only be adjusted for creator fulfilled products, please{' '}
                    <a
                      href="mailto:support@fourthwall.com"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      reach out to support
                    </a>{' '}
                    for any questions.
                  </>
                }
              />
            </S.SingleVariantItem>
          )}
          <S.SingleVariantItem>
            <S.SingleVariantLabel>In stock:</S.SingleVariantLabel>
            <StockTooltip
              quantity={firstVariantQuantity}
              disabled={!viewConfig.stockEditable || hasDisabledInventoryQuantity}
              onChange={onStockChange(data[0].id)}
            />
          </S.SingleVariantItem>
          {viewConfig.showExternalStock && (
            <S.SingleVariantItem>
              <S.SingleVariantLabel>In fulfillment center:</S.SingleVariantLabel>
              {data[0].externalStock}
            </S.SingleVariantItem>
          )}
          {viewConfig.showWeight && (
            <S.SingleVariantItem>
              <S.SingleVariantLabel>Weight:</S.SingleVariantLabel>
              <S.WeightInputWrapper>
                <WeightInput
                  weightName="variants[0].weight.value"
                  unitName="variants[0].weight.unit"
                  size="small"
                  weightWidth={80}
                  unitWidth={76}
                  disabled={!viewConfig.editable}
                />
              </S.WeightInputWrapper>
            </S.SingleVariantItem>
          )}
        </S.SingleVariantContainer>
      )
    }

    return (
      <S.DataContainer>
        <ProductVariantsData
          data={data}
          showWeight={viewConfig.showVariantsWeight}
          showPricing={viewConfig.showPricing}
          showExternalStock={viewConfig.showExternalStock}
          disabled={!viewConfig.editable}
          stockDisabled={!viewConfig.stockEditable}
          onStockTooltipChange={onStockChange}
        />
      </S.DataContainer>
    )
  }

  const renderAddVariantTypeButton = () => {
    if (!viewConfig.variantsEditable || !viewConfig.editable) return null

    const label = viewConfig.showVariantsTable
      ? 'Edit, add or remove variants'
      : 'Add variants (different colors, sizes, etc.)'

    return <ButtonClean label={label} onClick={onVariantTypeAdd} />
  }

  return (
    <S.Container>
      <Subsection
        title={viewConfig.showVariantsTable ? 'Inventory and variants' : 'Inventory'}
        borderless={viewConfig.showVariantsTable}
      >
        {renderContent()}
        {renderAddVariantTypeButton()}
        <S.Footer>
          {viewConfig.showPricing && (
            <CompareAtPriceCheckboxField
              onValueChange={onIsCompareAtPriceEnabledValueChange}
              disabled={!viewConfig.editable}
            />
          )}
          <Field
            name="isSoldOut"
            label="Mark as sold out"
            component={Checkbox}
            disabled={!viewConfig.editable}
          />
          {viewConfig.variantsEditable && (
            <>
              <Field
                name="visualHints.customSkus"
                component={Checkbox}
                label={
                  viewConfig.showVariantsTable
                    ? 'Each variant has an identification code (such as a UPC or SKU #)'
                    : 'This product has an identification code (such as a UPC or SKU #)'
                }
                disabled={!viewConfig.editable}
              />
              {viewConfig.showSingleSkuInput && (
                <S.FooterInputContainer>
                  <Field
                    component={Input}
                    name="variants[0].sku"
                    label="SKU or UPC code"
                    disabled={!viewConfig.editable}
                    onValueChange={handleSkuChange}
                  />
                </S.FooterInputContainer>
              )}
            </>
          )}
          <ProductMembershipSettings
            membershipTiers={membershipTiers}
            disabled={!viewConfig.editable}
          />
        </S.Footer>
      </Subsection>
    </S.Container>
  )
}
